// js/store.js

import { loadXML } from "./xmlLoader.js";
import { normalizeLIDO } from "./normalize.js";

/** -------------------------------------------------------------
 * Internal state
 * --------------------------------------------------------------*/

/**
 * @typedef {Object} Filters
 * @property {string[]} categories - Selected category labels to filter by.
 * @property {string} incipitPattern - Keyboard/typed sequence (e.g. "CFFG" or "C4F4F4G4").
 * @property {"start"|"any"} position - Match at the beginning ("start") or anywhere ("any").
 * @property {boolean} transpose - If true, match by intervals (transpose-insensitive).
 */

/**
 * @typedef {Object} Item
 * @property {string} id
 * @property {string} title
 * @property {string} date
 * @property {string[]} categories
 * @property {string[]} tags
 * @property {{ paeInline?: string }} incipit
 * @property {{ images: Array<{ src:string, type?:string, alt?:string }> }} media
 * // ...plus other normalized LIDO fields
 */

/** @type {{ items: Item[]; loaded: boolean; filters: Filters }} */
const state = {
  items: [],
  loaded: false,
  filters: {
    categories: [],
    incipitPattern: "",
    position: "start",
    transpose: false,
  },
};

/** Simple pub/sub for store events */
const listeners = {
  "data:ready": new Set(),
  "filter:changed": new Set(),
};

/** -------------------------------------------------------------
 * Event helpers
 * --------------------------------------------------------------*/

/**
 * Emit a store event with a payload.
 * @param {"data:ready"|"filter:changed"} event
 * @param {any} detail
 */
function emit(event, detail) {
  (listeners[event] || []).forEach((fn) => {
    try {
      fn(detail);
    } catch (e) {
      console.error(e);
    }
  });
}

/**
 * Subscribe to a store event.
 * @param {"data:ready"|"filter:changed"} event
 * @param {(detail:any) => void} fn
 * @returns {() => void} Unsubscribe function
 */
export function on(event, fn) {
  if (!listeners[event]) listeners[event] = new Set();
  listeners[event].add(fn);
  return () => listeners[event].delete(fn);
}

/** -------------------------------------------------------------
 * Public getters
 * --------------------------------------------------------------*/

/**
 * Get a shallow copy of all normalized items.
 * @returns {Item[]}
 */
export function getItems() {
  return state.items.slice();
}

/**
 * Get a shallow copy of current filters.
 * @returns {Filters}
 */
export function getFilters() {
  return { ...state.filters };
}

/** -------------------------------------------------------------
 * Data loading
 * --------------------------------------------------------------*/

/**
 * Load all XML files listed in a JSON manifest, normalize to items, and emit "data:ready".
 * Idempotent: subsequent calls are no-ops once loaded.
 * @param {{manifestUrl?: string}} [opts]
 * @returns {Promise<void>}
 */
export async function loadAll({ manifestUrl = "/data/manifest.json" } = {}) {
  if (state.loaded) return;

  const res = await fetch(manifestUrl, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Manifest load failed: ${res.status}`);
  /** @type {string[]} */
  const files = await res.json();

  /** @param {string} p */
  function dirname(p) {
    const i = p.lastIndexOf("/");
    return i >= 0 ? p.slice(0, i + 1) : "";
  }

  const items = [];
  for (const rel of files) {
    const xmlPath = `/data/${rel}.xml`;
    try {
      const xml = await loadXML(xmlPath);
      const baseHref = `/${dirname(`data/${rel}`)}`;
      const batch = normalizeLIDO(xml, { imageBaseHref: baseHref });
      items.push(...batch);
    } catch (err) {
      console.warn("Failed to load", xmlPath, err);
    }
  }

  state.items = items;
  state.loaded = true;
  emit("data:ready", { items: getItems() });
}

/** -------------------------------------------------------------
 * Incipit parsing & matching utilities
 * --------------------------------------------------------------*/

/**
 * Convert a note name (e.g., "C", "F#", "Bb") to pitch-class (0–11).
 * Accepts German H → B.
 * @param {string} name
 * @returns {number|null}
 */
function nameToPC(name) {
  let n = String(name || "").trim();
  if (!n) return null;
  n = n.replace(/^H/i, "B"); // German spelling: H == B

  const m = n.match(/^([A-Ga-g])([#b]?)/);
  if (!m) return null;

  const letter = m[1].toUpperCase();
  const acc = m[2] || "";
  const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[letter];
  let pc = base;
  if (acc === "#") pc = (pc + 1) % 12;
  if (acc === "b") pc = (pc + 11) % 12;
  return pc;
}

/**
 * Parse a keyboard/typed pattern (e.g., "CFFG" or "C4F4F4G4") into pitch-classes.
 * Ignores octave numbers.
 * @param {string} str
 * @returns {number[]}
 */
function pcsFromPatternString(str) {
  const tokens = (str.match(/[A-Ga-g](?:#|b)?\d?/g) || []).map((t) =>
    t.replace(/\d+$/, "")
  );
  return tokens.map(nameToPC).filter((v) => v !== null);
}

/**
 * Extract pitch-classes from a Plaine & Easie string.
 * Supports Verovio @key:value format (uses @data: block if present).
 * Normalizes '-'→'b', '+'→'#', and H→B; ignores octave/duration/quotes.
 * @param {string} pae
 * @returns {number[]}
 */
function pcsFromPAE(pae) {
  let data = (pae || "").trim();
  const m = data.match(/@data:([\s\S]+)/i);
  if (m) data = m[1];

  data = data.replace(/-/g, "b").replace(/\+/g, "#").replace(/\bH/gi, "B");
  const tokens = data.match(/[A-Ga-g](?:#|b|n)?/g) || [];

  const out = [];
  for (let t of tokens) {
    t = t.replace(/n$/i, ""); // drop 'natural' marker
    const pc = nameToPC(t);
    if (pc !== null) out.push(pc);
  }
  return out;
}

/**
 * Convert a pitch-class sequence into interval steps (mod 12).
 * @param {number[]} seq
 * @returns {number[]}
 */
function intervals(seq) {
  const iv = [];
  for (let i = 1; i < seq.length; i++) iv.push((seq[i] - seq[i - 1] + 12) % 12);
  return iv;
}

/**
 * Match a pattern (as PCs) against a target (as PCs).
 * If transpose=false → exact PCs; if transpose=true → interval sequence match.
 * @param {number[]} targetPCs
 * @param {number[]} pattPCs
 * @param {{startOnly:boolean, transpose:boolean}} opts
 * @returns {boolean}
 */
function matchPattern(targetPCs, pattPCs, { startOnly, transpose }) {
  if (!pattPCs.length) return true;
  if (!targetPCs.length) return false;

  if (!transpose) {
    // exact pitch-class sequence
    const n = pattPCs.length;
    const m = targetPCs.length;
    const hi = startOnly ? 0 : m - n;
    for (let i = 0; i <= hi; i++) {
      let ok = true;
      for (let j = 0; j < n; j++) {
        if (targetPCs[i + j] !== pattPCs[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return true;
      if (startOnly) break;
    }
    return false;
  }

  // interval (transpose-insensitive) matching
  if (pattPCs.length === 1) {
    return startOnly
      ? targetPCs[0] === pattPCs[0]
      : targetPCs.includes(pattPCs[0]);
  }
  const patIv = intervals(pattPCs);
  const tgtIv = intervals(targetPCs);
  const n = patIv.length;
  const m = tgtIv.length;
  const hi = startOnly ? 0 : m - n;
  for (let i = 0; i <= hi; i++) {
    let ok = true;
    for (let j = 0; j < n; j++) {
      if (tgtIv[i + j] !== patIv[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return true;
    if (startOnly) break;
  }
  return false;
}

/** -------------------------------------------------------------
 * Filtering
 * --------------------------------------------------------------*/

/**
 * Apply all filters (incipit first, then categories if provided).
 * - Incipit filter automatically restricts to items that have `incipit.paeInline`.
 * - Categories are applied only if any are selected.
 * @param {Item[]} items
 * @param {Filters} filters
 * @returns {Item[]}
 */
function applyAllFilters(items, filters) {
  let out = items;

  // Incipit (dominant)
  const patt = (filters.incipitPattern || "").trim();
  if (patt) {
    const pattPCs = pcsFromPatternString(patt);
    const startOnly = filters.position === "start";
    const transpose = !!filters.transpose;

    out = out.filter((it) => {
      const pae = it?.incipit?.paeInline;
      if (!pae) return false;
      const tgtPCs = pcsFromPAE(pae);
      return matchPattern(tgtPCs, pattPCs, { startOnly, transpose });
    });
  }

  // Categories (optional narrowing)
  const cats = new Set((filters.categories || []).filter(Boolean));
  if (cats.size) {
    out = out.filter((it) => (it.categories || []).some((c) => cats.has(c)));
  }

  return out;
}

/**
 * Update filters and emit "filter:changed" with the current result set.
 * Idempotent partial updates (merge into existing filters).
 * @param {Partial<Filters>} [partial]
 */
export function setFilter(partial = {}) {
  state.filters = { ...state.filters, ...partial };
  const base = state.items; // stable base set
  const filtered = applyAllFilters(base, state.filters);
  emit("filter:changed", { filters: getFilters(), filtered });
}
